'use client';

import React from 'react';
import type { ReportType } from './ReportsHub';
import StatusBadge from '@/components/ui/StatusBadge';

interface ReportPreviewProps {
  reportType: ReportType;
  dateFrom: string;
  dateTo: string;
}

const formatVT = (n: number) => `VT ${n.toLocaleString('en-US')}`;
const formatPct = (n: number) => `${n.toFixed(1)}%`;

// ── Mock data per report type ──────────────────────────────────────────────

// Backend integration point: replace each dataset with /api/reports/[type]?from=&to=

const investorCapitalData = [
  { id: 'inv-001', name: 'Timoci Rabukawaqa', phone: '+678 7712 334', contributed: 2000000, totalContributions: 2500000, profitShare: 29.4, profitEarned: 218750, status: 'active', joinDate: '12 Jan 2024' },
  { id: 'inv-002', name: 'Kuini Naitini', phone: '+678 5541 882', contributed: 1500000, totalContributions: 1750000, profitShare: 20.6, profitEarned: 152250, status: 'active', joinDate: '03 Mar 2024' },
  { id: 'inv-003', name: 'Anare Batibasaga', phone: '+678 7893 115', contributed: 1200000, totalContributions: 1200000, profitShare: 14.1, profitEarned: 96750, status: 'active', joinDate: '22 Jun 2024' },
  { id: 'inv-004', name: 'Vasiti Leweniqila', phone: '+678 5234 771', contributed: 1000000, totalContributions: 1050000, profitShare: 12.4, profitEarned: 87500, status: 'active', joinDate: '14 Aug 2024' },
  { id: 'inv-005', name: 'Isikeli Qereqeretabua', phone: '+678 7741 203', contributed: 800000, totalContributions: 800000, profitShare: 9.4, profitEarned: 70000, status: 'active', joinDate: '01 Oct 2024' },
  { id: 'inv-006', name: 'Seini Talemaitoga', phone: '+678 5312 940', contributed: 600000, totalContributions: 600000, profitShare: 7.1, profitEarned: 52500, status: 'active', joinDate: '15 Nov 2024' },
  { id: 'inv-007', name: 'Ratu Naivalu', phone: '+678 7823 556', contributed: 250000, totalContributions: 350000, profitShare: 4.1, profitEarned: 28000, status: 'active', joinDate: '20 Jan 2025' },
  { id: 'inv-008', name: 'Mere Vunisa', phone: '+678 5578 291', contributed: 150000, totalContributions: 150000, profitShare: 2.9, profitEarned: 0, status: 'inactive', joinDate: '04 Apr 2025' },
];

const activeLoansData = [
  { id: 'loan-0043', loanNo: 'VL-2026-043', borrower: 'Mosese Digitaki', principal: 400000, interest: 80000, totalDue: 480000, outstanding: 378000, rate: 20, term: '12 months', startDate: '15 Jan 2026', dueDate: '15 Jan 2027', frequency: 'Monthly', status: 'active' },
  { id: 'loan-0047', loanNo: 'VL-2026-047', borrower: 'Asenaca Koroi', principal: 300000, interest: 60000, totalDue: 360000, outstanding: 240000, rate: 20, term: '12 months', startDate: '01 Feb 2026', dueDate: '01 Feb 2027', frequency: 'Monthly', status: 'active' },
  { id: 'loan-0049', loanNo: 'VL-2026-049', borrower: 'Salote Tuilagi', principal: 175000, interest: 35000, totalDue: 210000, outstanding: 175000, rate: 20, term: '6 months', startDate: '10 Mar 2026', dueDate: '10 Sep 2026', frequency: 'Fortnightly', status: 'active' },
  { id: 'loan-0051', loanNo: 'VL-2026-051', borrower: 'Litiana Bose', principal: 250000, interest: 50000, totalDue: 300000, outstanding: 300000, rate: 20, term: '12 months', startDate: '05 Apr 2026', dueDate: '05 Apr 2027', frequency: 'Monthly', status: 'active' },
  { id: 'loan-0053', loanNo: 'VL-2026-053', borrower: 'Viliame Cakobau', principal: 500000, interest: 100000, totalDue: 600000, outstanding: 583000, rate: 20, term: '18 months', startDate: '12 Apr 2026', dueDate: '12 Oct 2027', frequency: 'Monthly', status: 'active' },
  { id: 'loan-0055', loanNo: 'VL-2026-055', borrower: 'Laisa Taukei', principal: 120000, interest: 24000, totalDue: 144000, outstanding: 132000, rate: 20, term: '6 months', startDate: '20 May 2026', dueDate: '20 Nov 2026', frequency: 'Weekly', status: 'active' },
  { id: 'loan-0056', loanNo: 'VL-2026-056', borrower: 'Naomi Ravuiwasa', principal: 350000, interest: 70000, totalDue: 420000, outstanding: 420000, rate: 20, term: '12 months', startDate: '01 Jun 2026', dueDate: '01 Jun 2027', frequency: 'Monthly', status: 'active' },
  { id: 'loan-0057', loanNo: 'VL-2026-057', borrower: 'Josaia Tuiloma', principal: 200000, interest: 40000, totalDue: 240000, outstanding: 240000, rate: 20, term: '6 months', startDate: '15 Jul 2026', dueDate: '15 Jan 2027', frequency: 'Fortnightly', status: 'active' },
  { id: 'loan-0058', loanNo: 'VL-2026-058', borrower: 'Mere Talua', principal: 80000, interest: 16000, totalDue: 96000, outstanding: 96000, rate: 20, term: '3 months', startDate: '22 Jul 2026', dueDate: '22 Oct 2026', frequency: 'Weekly', status: 'active' },
  { id: 'loan-0059', loanNo: 'VL-2026-059', borrower: 'Epeli Vonokula', principal: 650000, interest: 130000, totalDue: 780000, outstanding: 780000, rate: 20, term: '24 months', startDate: '01 Aug 2026', dueDate: '01 Aug 2028', frequency: 'Monthly', status: 'active' },
];

const overdueLoansData = [
  { id: 'loan-0029', loanNo: 'VL-2026-029', borrower: 'Seru Nakauvadra', phone: '+678 5318 664', principal: 300000, outstanding: 165000, daysOverdue: 7, lastPayment: '26 Jun 2026', missedPayments: 1, status: 'overdue' },
  { id: 'loan-0031', loanNo: 'VL-2026-031', borrower: 'Asenaca Koroi', phone: '+678 7741 229', principal: 200000, outstanding: 200000, daysOverdue: 3, lastPayment: '01 Jul 2026', missedPayments: 1, status: 'overdue' },
  { id: 'loan-0038', loanNo: 'VL-2026-038', borrower: 'Mere Talua', phone: '+678 7743 291', principal: 150000, outstanding: 98500, daysOverdue: 12, lastPayment: '21 Jun 2026', missedPayments: 2, status: 'overdue' },
  { id: 'loan-0041', loanNo: 'VL-2026-041', borrower: 'Kalo Vanuakula', phone: '+678 5521 847', principal: 250000, outstanding: 142000, daysOverdue: 18, lastPayment: '15 Jun 2026', missedPayments: 3, status: 'overdue' },
];

const interestIncomeData = [
  { id: 'int-jan', period: 'January 2026', loansActive: 14, interestDue: 112000, interestCollected: 98400, interestOutstanding: 13600, collectionRate: 87.9 },
  { id: 'int-feb', period: 'February 2026', loansActive: 16, interestDue: 128000, interestCollected: 121600, interestOutstanding: 6400, collectionRate: 95.0 },
  { id: 'int-mar', period: 'March 2026', loansActive: 17, interestDue: 136000, interestCollected: 128920, interestOutstanding: 7080, collectionRate: 94.8 },
  { id: 'int-apr', period: 'April 2026', loansActive: 19, interestDue: 152000, interestCollected: 144400, interestOutstanding: 7600, collectionRate: 95.0 },
  { id: 'int-may', period: 'May 2026', loansActive: 20, interestDue: 160000, interestCollected: 148800, interestOutstanding: 11200, collectionRate: 93.0 },
  { id: 'int-jun', period: 'June 2026', loansActive: 21, interestDue: 168000, interestCollected: 160000, interestOutstanding: 8000, collectionRate: 95.2 },
  { id: 'int-jul', period: 'July 2026', loansActive: 22, interestDue: 176000, interestCollected: 168000, interestOutstanding: 8000, collectionRate: 95.5 },
  { id: 'int-aug', period: 'August 2026 (MTD)', loansActive: 23, interestDue: 48000, interestCollected: 31200, interestOutstanding: 16800, collectionRate: 65.0 },
];

const monthlyCollectionsData = [
  { id: 'col-jan', month: 'January 2026', payments: 42, totalCollected: 687000, principalPaid: 574000, interestPaid: 113000, onTime: 38, late: 4 },
  { id: 'col-feb', month: 'February 2026', payments: 48, totalCollected: 812000, principalPaid: 690000, interestPaid: 122000, onTime: 45, late: 3 },
  { id: 'col-mar', month: 'March 2026', payments: 51, totalCollected: 924000, principalPaid: 796000, interestPaid: 128000, onTime: 48, late: 3 },
  { id: 'col-apr', month: 'April 2026', payments: 57, totalCollected: 1048000, principalPaid: 896000, interestPaid: 152000, onTime: 54, late: 3 },
  { id: 'col-may', month: 'May 2026', payments: 60, totalCollected: 1124000, principalPaid: 964000, interestPaid: 160000, onTime: 56, late: 4 },
  { id: 'col-jun', month: 'June 2026', payments: 63, totalCollected: 1196000, principalPaid: 1028000, interestPaid: 168000, onTime: 60, late: 3 },
  { id: 'col-jul', month: 'July 2026', payments: 66, totalCollected: 1312000, principalPaid: 1144000, interestPaid: 168000, onTime: 62, late: 4 },
  { id: 'col-aug', month: 'August 2026 (MTD)', payments: 14, totalCollected: 312800, principalPaid: 281600, interestPaid: 31200, onTime: 13, late: 1 },
];

const profitDistributionData = [
  { id: 'pd-001', investor: 'Timoci Rabukawaqa', profitShare: 29.4, grossProfit: 218750, taxWithheld: 10937, netDistribution: 207813, lastDistributed: '31 Jul 2026', ytdTotal: 1531250 },
  { id: 'pd-002', investor: 'Kuini Naitini', profitShare: 20.6, grossProfit: 152250, taxWithheld: 7612, netDistribution: 144638, lastDistributed: '31 Jul 2026', ytdTotal: 1065750 },
  { id: 'pd-003', investor: 'Anare Batibasaga', profitShare: 14.1, grossProfit: 96750, taxWithheld: 4837, netDistribution: 91913, lastDistributed: '31 Jul 2026', ytdTotal: 677250 },
  { id: 'pd-004', investor: 'Vasiti Leweniqila', profitShare: 12.4, grossProfit: 87500, taxWithheld: 4375, netDistribution: 83125, lastDistributed: '31 Jul 2026', ytdTotal: 612500 },
  { id: 'pd-005', investor: 'Isikeli Qereqeretabua', profitShare: 9.4, grossProfit: 70000, taxWithheld: 3500, netDistribution: 66500, lastDistributed: '31 Jul 2026', ytdTotal: 490000 },
  { id: 'pd-006', investor: 'Seini Talemaitoga', profitShare: 7.1, grossProfit: 52500, taxWithheld: 2625, netDistribution: 49875, lastDistributed: '31 Jul 2026', ytdTotal: 367500 },
  { id: 'pd-007', investor: 'Ratu Naivalu', profitShare: 4.1, grossProfit: 28000, taxWithheld: 1400, netDistribution: 26600, lastDistributed: '31 Jul 2026', ytdTotal: 196000 },
  { id: 'pd-008', investor: 'Mere Vunisa', profitShare: 2.9, grossProfit: 0, taxWithheld: 0, netDistribution: 0, lastDistributed: '—', ytdTotal: 0 },
];

const cashFlowData = [
  { id: 'cf-jan', period: 'January 2026', capitalIn: 500000, loansOut: 1100000, repaymentsIn: 687000, distributionsOut: 125000, netFlow: -38000 },
  { id: 'cf-feb', period: 'February 2026', capitalIn: 0, loansOut: 960000, repaymentsIn: 812000, distributionsOut: 125000, netFlow: -273000 },
  { id: 'cf-mar', period: 'March 2026', capitalIn: 200000, loansOut: 1240000, repaymentsIn: 924000, distributionsOut: 125000, netFlow: -241000 },
  { id: 'cf-apr', period: 'April 2026', capitalIn: 0, loansOut: 880000, repaymentsIn: 1048000, distributionsOut: 125000, netFlow: 43000 },
  { id: 'cf-may', period: 'May 2026', capitalIn: 300000, loansOut: 1380000, repaymentsIn: 1124000, distributionsOut: 125000, netFlow: -81000 },
  { id: 'cf-jun', period: 'June 2026', capitalIn: 0, loansOut: 1150000, repaymentsIn: 1196000, distributionsOut: 125000, netFlow: -79000 },
  { id: 'cf-jul', period: 'July 2026', capitalIn: 500000, loansOut: 1480000, repaymentsIn: 1312000, distributionsOut: 125000, netFlow: 207000 },
  { id: 'cf-aug', period: 'August 2026 (MTD)', capitalIn: 0, loansOut: 430000, repaymentsIn: 312800, distributionsOut: 0, netFlow: -117200 },
];

// ── Summary metrics per report ─────────────────────────────────────────────

function SummaryMetrics({ reportType }: { reportType: ReportType }) {
  const summaries: Record<ReportType, { label: string; value: string; color?: string }[]> = {
    'investor-capital': [
      { label: 'Total Invested', value: 'VT 8,500,000' },
      { label: 'Active Investors', value: '7 of 8' },
      { label: 'Total Profit Paid', value: 'VT 705,750', color: 'text-positive' },
      { label: 'Avg Profit Share', value: '12.5%' },
    ],
    'active-loans': [
      { label: 'Active Loans', value: '23' },
      { label: 'Total Principal', value: 'VT 5,220,000' },
      { label: 'Total Outstanding', value: 'VT 6,144,000' },
      { label: 'Avg Loan Size', value: 'VT 226,957' },
    ],
    'overdue-loans': [
      { label: 'Overdue Loans', value: '4', color: 'text-negative' },
      { label: 'Total Overdue Balance', value: 'VT 487,000', color: 'text-negative' },
      { label: 'Avg Days Overdue', value: '10 days' },
      { label: 'Missed Payments', value: '7' },
    ],
    'interest-income': [
      { label: 'Total Interest Due', value: 'VT 1,080,000' },
      { label: 'Total Collected', value: 'VT 1,000,920', color: 'text-positive' },
      { label: 'Outstanding', value: 'VT 79,080', color: 'text-warning' },
      { label: 'Avg Collection Rate', value: '92.7%' },
    ],
    'monthly-collections': [
      { label: 'Total Payments', value: '401' },
      { label: 'Total Collected', value: 'VT 7,416,800', color: 'text-positive' },
      { label: 'On-Time Rate', value: '94.3%' },
      { label: 'Late Payments', value: '25', color: 'text-warning' },
    ],
    'profit-distribution': [
      { label: 'Total Gross Profit', value: 'VT 705,750' },
      { label: 'Tax Withheld', value: 'VT 35,286' },
      { label: 'Net Distributed', value: 'VT 670,464', color: 'text-positive' },
      { label: 'Pending Distribution', value: 'VT 0' },
    ],
    'cash-flow': [
      { label: 'Total Capital In', value: 'VT 1,500,000', color: 'text-positive' },
      { label: 'Total Loans Out', value: 'VT 8,620,000' },
      { label: 'Total Repayments In', value: 'VT 7,416,800', color: 'text-positive' },
      { label: 'Net Cash Flow', value: '-VT 578,200', color: 'text-warning' },
    ],
  };

  const items = summaries[reportType];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 px-5 py-4 border-b border-border bg-muted/20">
      {items.map((item) => (
        <div key={`summary-${reportType}-${item.label}`} className="space-y-0.5">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{item.label}</p>
          <p className={`text-base font-bold font-mono tabular-nums ${item.color ?? 'text-foreground'}`}>{item.value}</p>
        </div>
      ))}
    </div>
  );
}

// ── Table renderers ────────────────────────────────────────────────────────

function InvestorCapitalTable() {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/30">
            {['Investor', 'Phone', 'Joined', 'Contributed', 'Total Contributions', 'Profit Share', 'Profit Earned', 'Status'].map((h) => (
              <th key={`th-ic-${h}`} className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {investorCapitalData.map((row) => (
            <tr key={row.id} className="hover:bg-muted/20 transition-colors">
              <td className="px-4 py-3 text-xs font-semibold text-foreground whitespace-nowrap">{row.name}</td>
              <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap font-mono">{row.phone}</td>
              <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{row.joinDate}</td>
              <td className="px-4 py-3 text-xs font-mono text-foreground whitespace-nowrap">{formatVT(row.contributed)}</td>
              <td className="px-4 py-3 text-xs font-mono text-foreground whitespace-nowrap">{formatVT(row.totalContributions)}</td>
              <td className="px-4 py-3 text-xs font-mono text-info whitespace-nowrap">{formatPct(row.profitShare)}</td>
              <td className="px-4 py-3 text-xs font-mono text-positive whitespace-nowrap">{formatVT(row.profitEarned)}</td>
              <td className="px-4 py-3"><StatusBadge status={row.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ActiveLoansTable() {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/30">
            {['Loan No.', 'Borrower', 'Principal', 'Interest', 'Total Due', 'Outstanding', 'Rate', 'Term', 'Due Date', 'Frequency', 'Status'].map((h) => (
              <th key={`th-al-${h}`} className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {activeLoansData.map((row) => (
            <tr key={row.id} className="hover:bg-muted/20 transition-colors">
              <td className="px-4 py-3 text-[11px] font-mono text-muted-foreground whitespace-nowrap">{row.loanNo}</td>
              <td className="px-4 py-3 text-xs font-semibold text-foreground whitespace-nowrap">{row.borrower}</td>
              <td className="px-4 py-3 text-xs font-mono text-foreground whitespace-nowrap">{formatVT(row.principal)}</td>
              <td className="px-4 py-3 text-xs font-mono text-warning whitespace-nowrap">{formatVT(row.interest)}</td>
              <td className="px-4 py-3 text-xs font-mono text-foreground whitespace-nowrap">{formatVT(row.totalDue)}</td>
              <td className="px-4 py-3 text-xs font-mono font-bold text-foreground whitespace-nowrap">{formatVT(row.outstanding)}</td>
              <td className="px-4 py-3 text-xs font-mono text-info whitespace-nowrap">{row.rate}%</td>
              <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{row.term}</td>
              <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{row.dueDate}</td>
              <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{row.frequency}</td>
              <td className="px-4 py-3"><StatusBadge status={row.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OverdueLoansTable() {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/30">
            {['Loan No.', 'Borrower', 'Phone', 'Principal', 'Outstanding', 'Days Overdue', 'Last Payment', 'Missed Payments', 'Status'].map((h) => (
              <th key={`th-ol-${h}`} className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {overdueLoansData.map((row) => (
            <tr key={row.id} className="hover:bg-negative-subtle/30 transition-colors">
              <td className="px-4 py-3 text-[11px] font-mono text-muted-foreground whitespace-nowrap">{row.loanNo}</td>
              <td className="px-4 py-3 text-xs font-semibold text-foreground whitespace-nowrap">{row.borrower}</td>
              <td className="px-4 py-3 text-xs font-mono text-muted-foreground whitespace-nowrap">{row.phone}</td>
              <td className="px-4 py-3 text-xs font-mono text-foreground whitespace-nowrap">{formatVT(row.principal)}</td>
              <td className="px-4 py-3 text-xs font-mono font-bold text-negative whitespace-nowrap">{formatVT(row.outstanding)}</td>
              <td className="px-4 py-3">
                <span className="text-xs font-bold text-negative font-mono">{row.daysOverdue} days</span>
              </td>
              <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{row.lastPayment}</td>
              <td className="px-4 py-3 text-xs font-mono text-warning">{row.missedPayments}</td>
              <td className="px-4 py-3"><StatusBadge status={row.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InterestIncomeTable() {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/30">
            {['Period', 'Active Loans', 'Interest Due', 'Collected', 'Outstanding', 'Collection Rate'].map((h) => (
              <th key={`th-ii-${h}`} className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {interestIncomeData.map((row) => (
            <tr key={row.id} className="hover:bg-muted/20 transition-colors">
              <td className="px-4 py-3 text-xs font-semibold text-foreground whitespace-nowrap">{row.period}</td>
              <td className="px-4 py-3 text-xs font-mono text-foreground">{row.loansActive}</td>
              <td className="px-4 py-3 text-xs font-mono text-foreground whitespace-nowrap">{formatVT(row.interestDue)}</td>
              <td className="px-4 py-3 text-xs font-mono text-positive whitespace-nowrap">{formatVT(row.interestCollected)}</td>
              <td className="px-4 py-3 text-xs font-mono text-warning whitespace-nowrap">{formatVT(row.interestOutstanding)}</td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1.5 rounded-full bg-muted min-w-[60px]">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${row.collectionRate}%`,
                        backgroundColor: row.collectionRate >= 90 ? 'var(--positive)' : row.collectionRate >= 75 ? 'var(--warning)' : 'var(--negative)',
                      }}
                    />
                  </div>
                  <span className={`text-xs font-mono font-semibold ${row.collectionRate >= 90 ? 'text-positive' : row.collectionRate >= 75 ? 'text-warning' : 'text-negative'}`}>
                    {formatPct(row.collectionRate)}
                  </span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MonthlyCollectionsTable() {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/30">
            {['Month', 'Payments', 'Total Collected', 'Principal Paid', 'Interest Paid', 'On Time', 'Late'].map((h) => (
              <th key={`th-mc-${h}`} className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {monthlyCollectionsData.map((row) => (
            <tr key={row.id} className="hover:bg-muted/20 transition-colors">
              <td className="px-4 py-3 text-xs font-semibold text-foreground whitespace-nowrap">{row.month}</td>
              <td className="px-4 py-3 text-xs font-mono text-foreground">{row.payments}</td>
              <td className="px-4 py-3 text-xs font-mono font-bold text-positive whitespace-nowrap">{formatVT(row.totalCollected)}</td>
              <td className="px-4 py-3 text-xs font-mono text-foreground whitespace-nowrap">{formatVT(row.principalPaid)}</td>
              <td className="px-4 py-3 text-xs font-mono text-warning whitespace-nowrap">{formatVT(row.interestPaid)}</td>
              <td className="px-4 py-3 text-xs font-mono text-positive">{row.onTime}</td>
              <td className="px-4 py-3 text-xs font-mono text-negative">{row.late}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProfitDistributionTable() {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/30">
            {['Investor', 'Profit Share', 'Gross Profit', 'Tax Withheld', 'Net Distribution', 'Last Distributed', 'YTD Total'].map((h) => (
              <th key={`th-pd-${h}`} className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {profitDistributionData.map((row) => (
            <tr key={row.id} className="hover:bg-muted/20 transition-colors">
              <td className="px-4 py-3 text-xs font-semibold text-foreground whitespace-nowrap">{row.investor}</td>
              <td className="px-4 py-3 text-xs font-mono text-info">{formatPct(row.profitShare)}</td>
              <td className="px-4 py-3 text-xs font-mono text-foreground whitespace-nowrap">{row.grossProfit > 0 ? formatVT(row.grossProfit) : '—'}</td>
              <td className="px-4 py-3 text-xs font-mono text-warning whitespace-nowrap">{row.taxWithheld > 0 ? formatVT(row.taxWithheld) : '—'}</td>
              <td className="px-4 py-3 text-xs font-mono font-bold text-positive whitespace-nowrap">{row.netDistribution > 0 ? formatVT(row.netDistribution) : '—'}</td>
              <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{row.lastDistributed}</td>
              <td className="px-4 py-3 text-xs font-mono font-bold text-foreground whitespace-nowrap">{row.ytdTotal > 0 ? formatVT(row.ytdTotal) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CashFlowTable() {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/30">
            {['Period', 'Capital In', 'Loans Out', 'Repayments In', 'Distributions Out', 'Net Cash Flow'].map((h) => (
              <th key={`th-cf-${h}`} className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {cashFlowData.map((row) => (
            <tr key={row.id} className="hover:bg-muted/20 transition-colors">
              <td className="px-4 py-3 text-xs font-semibold text-foreground whitespace-nowrap">{row.period}</td>
              <td className="px-4 py-3 text-xs font-mono text-positive whitespace-nowrap">{row.capitalIn > 0 ? `+${formatVT(row.capitalIn)}` : '—'}</td>
              <td className="px-4 py-3 text-xs font-mono text-warning whitespace-nowrap">-{formatVT(row.loansOut)}</td>
              <td className="px-4 py-3 text-xs font-mono text-positive whitespace-nowrap">+{formatVT(row.repaymentsIn)}</td>
              <td className="px-4 py-3text-xs font-mono text-negative whitespace-nowrap">-{row.distributionsOut > 0 ? formatVT(row.distributionsOut) : '—'}</td>
              <td className="px-4 py-3">
                <span className={`text-xs font-mono font-bold ${row.netFlow >= 0 ? 'text-positive' : 'text-negative'}`}>
                  {row.netFlow >= 0 ? '+' : ''}{formatVT(row.netFlow)}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Main export ────────────────────────────────────────────────────────────

export default function ReportPreview({ reportType }: ReportPreviewProps) {
  const tableMap: Record<ReportType, React.ReactNode> = {
    'investor-capital': <InvestorCapitalTable />,
    'active-loans': <ActiveLoansTable />,
    'overdue-loans': <OverdueLoansTable />,
    'interest-income': <InterestIncomeTable />,
    'monthly-collections': <MonthlyCollectionsTable />,
    'profit-distribution': <ProfitDistributionTable />,
    'cash-flow': <CashFlowTable />,
  };

  return (
    <div className="flex flex-col">
      <SummaryMetrics reportType={reportType} />
      <div className="flex-1">
        {tableMap[reportType]}
      </div>
      <div className="px-5 py-3 border-t border-border flex items-center justify-between bg-muted/10">
        <p className="text-[11px] text-muted-foreground">
          VatuLend · Generated 03 Aug 2026, 05:33 WST · All amounts in Vanuatu Vatu (VT)
        </p>
        <p className="text-[11px] text-muted-foreground font-mono">
          Confidential — For authorised use only
        </p>
      </div>
    </div>
  );
}